/**
 * The wins list (SPEC.md §8.4).
 *
 * Shared by the briefing and the Wins page, because it is the same list and the briefing is
 * where it earns its keep — §8.4 calls this the thing that "makes the list something worth
 * revisiting", which it cannot do from behind a nav link.
 *
 * Hovering a row fires a burst at the cursor, scaled by what the task cost. §8.4's argument
 * for hover over load is voluntary versus involuntary: "animation you didn't ask for becomes
 * noise within a week, but animation you triggered by sweeping the cursor across the list
 * stays rewarding, because re-triggering it is a choice."
 */

import type { Completion, WinRange } from "../api";
import { Die } from "./Die";
import { Check, Star } from "./icons";
import { celebrateProject, useFireworks } from "./fireworksContext";

function timeOfDay(epoch: number): string {
  return new Date(epoch * 1000).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WinsList({
  completions,
  empty,
}: {
  completions: Completion[];
  empty?: string;
}) {
  const burst = useFireworks();

  if (completions.length === 0) {
    return empty ? <p className="muted small">{empty}</p> : null;
  }

  return (
    <ul className="wins-list">
      {completions.map((win, index) => (
        <li
          className={`win${win.kind === "project" ? " win-project" : ""}`}
          key={`${win.task_id}-${win.completed_at}`}
          data-testid="win"
          // Drives the §8.4 hover stagger. See the `.win:hover` rule in styles.css.
          style={{ "--i": index } as React.CSSProperties}
          onMouseEnter={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            // A task echoes once, at the level it cost — a sweep down the list should not
            // be a wall of noise. A finished project gets its full volley back, because
            // that is the one thing in here worth stopping for.
            if (win.kind === "project") {
              celebrateProject(burst);
            } else {
              burst(e.clientX, box.top + box.height / 2, win.energy);
            }
          }}
        >
          <span className="mark" aria-hidden="true">
            {win.kind === "project" ? <Star filled size={18} /> : <Check size={18} />}
          </span>
          <span className="title">
            <span className="text">
              {win.title}
              {win.kind === "project" && <span className="badge project"> project</span>}
            </span>
            <span className="meta">
              {/* §8.6.4: a subtask completion shows its parent as context — "which
                  breakdown was this a piece of" is the thing worth reading here, not the
                  area every row in the list already shares more often than not. */}
              {win.parent_title ? (
                <span className="badge" title="Part of this breakdown">
                  {win.parent_title}
                </span>
              ) : (
                win.area_name && <span className="badge">{win.area_name}</span>
              )}
              {win.kind === "project" ? (
                win.outcome && <span className="muted">{win.outcome}</span>
              ) : (
                <span className="badge energy">
                  <Die value={win.energy} />
                </span>
              )}
              <span>{timeOfDay(win.completed_at)}</span>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** §8.4: "Lead with counts — a number you can compare does work that styling can't." */
export function WinCounts({
  counts,
  onSelect,
  selected,
}: {
  counts: Record<string, number>;
  onSelect?: (range: WinRange) => void;
  selected?: string;
}) {
  // `spoken` exists because the rolling ranges are abbreviated to fit all four counts on
  // one row on a phone. "7d" is clear enough to read at a glance and useless to hear.
  const entries: { id: WinRange; label: string; spoken?: string }[] = [
    { id: "today", label: "Today" },
    { id: "yesterday", label: "Yesterday" },
    { id: "7d", label: "7d", spoken: "last 7 days" },
    { id: "30d", label: "30d", spoken: "last 30 days" },
  ];

  return (
    <div className="counts">
      {entries.map((entry) => {
        const body = (
          <>
            <span className="n">{counts[entry.id] ?? 0}</span>
            <span className="label" title={entry.spoken}>
              {entry.label}
            </span>
          </>
        );
        return onSelect ? (
          <button
            key={entry.id}
            className="count count-button"
            aria-label={`${counts[entry.id] ?? 0} in ${entry.spoken ?? entry.label}`}
            aria-pressed={selected === entry.id}
            onClick={() => onSelect(entry.id)}
          >
            {body}
          </button>
        ) : (
          <span className="count" key={entry.id}>
            {body}
          </span>
        );
      })}
    </div>
  );
}
